import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {catchError, Observable} from 'rxjs';
import {GridGetService, GridServiceParams, GridServiceResponse} from 'src/app/shared/model/page-grid';
import {removeEmptyParams} from 'src/infrastructure/helper/remove-empty-params';
import moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class AdxInvoiceLineItemGridService implements GridGetService<any> {
  private readonly baseUrl = 'dbov2/adx-invoice-line-item';
  private readonly http = inject(HttpClient);

  public DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSS[Z]';

  public getExportFilters(filters: any): Record<string, any> {
    const exportFilters: Record<string, any> = { ...filters };
    if (filters['issueStartDate']) {
      exportFilters['issueStartDate'] = moment(filters['issueStartDate']).format('YYYY-MM-DD');
    }
    if (filters['issueEndDate']) {
      exportFilters['issueEndDate'] = moment(filters['issueEndDate']).format('YYYY-MM-DD');
    }
    if (filters['createdStartDate']) {
      exportFilters['createdStartDate'] = moment(filters['createdStartDate']).format('YYYY-MM-DD');
    }
    if (filters['createdEndDate']) {
      exportFilters['createdEndDate'] = moment(filters['createdEndDate']).format('YYYY-MM-DD');
    }
    return exportFilters;
  }

  public getAutocompleteValue(value: any): string {
    return this.getFilterValue(value);
  }

  getItems({filters, page, sort} : GridServiceParams): Observable<GridServiceResponse<any>>  {
    const params:Record<string, string | number | boolean> = {
      pageSize: page?.pageSize || 25,
      pageNumber: (page?.pageIndex || 0) + 1,
      propertyName: sort?.active || '',
      orderKey: sort?.direction || '',
    };

    if (filters && typeof filters === 'object' && Object.keys(filters).length > 0) {

      try {
        const quoteIdValue = this.getFilterValue(filters['quoteID']);

        if (quoteIdValue && quoteIdValue.trim() !== '') {
          const numericQuoteId = parseInt(quoteIdValue);
          if (!isNaN(numericQuoteId)) {
            params['QuoteId'] = numericQuoteId;
          } else {
            params['QuoteId'] = quoteIdValue;
          }
        }
      } catch (error) {
        console.error('Error processing quoteID:', error);
      }

      try {
        const invoiceRecordLocatorValue = this.getFilterValue(filters['invoiceRecordLocator']);

        if (invoiceRecordLocatorValue && invoiceRecordLocatorValue.trim() !== '') {
          params['InvoiceRecordLocator'] = invoiceRecordLocatorValue;
        }
      } catch (error) {
        console.error('Error processing invoiceRecordLocator:', error);
      }

      try {
        const invoiceTypeValue = this.getFilterValue(filters['invoiceType']);

        if (invoiceTypeValue && invoiceTypeValue.trim() !== '') {
          params['InvoiceType'] = invoiceTypeValue;
        }
      } catch (error) {
        console.error('Error processing invoiceType:', error);
      }

      try {
        const clientProfileNoValue = this.getFilterValue(filters['clientProfileNo']);

        if (clientProfileNoValue && clientProfileNoValue.trim() !== '') {
          params['ClientProfileNo'] = clientProfileNoValue;
        }
      } catch (error) {
        console.error('Error processing clientProfileNo:', error);
      }

      if (filters['issueStartDate']) {
        params['IssueStartDate'] = moment(filters['issueStartDate']).format('YYYY-MM-DD');
      }

      if (filters['issueEndDate']) {
        params['IssueEndDate'] = moment(filters['issueEndDate']).format('YYYY-MM-DD');
      }

      if (filters['createdStartDate']) {
        params['CreatedStartDate'] = moment(filters['createdStartDate']).format('YYYY-MM-DD');
      }

      if (filters['createdEndDate']) {
        params['CreatedEndDate'] = moment(filters['createdEndDate']).format('YYYY-MM-DD');
      }
    }

    removeEmptyParams(params);

    return this.http.get<GridServiceResponse<any>>(`${this.baseUrl}`, { params: {...params} })
    .pipe(catchError((error:HttpErrorResponse)=>{
        throw new Error(error?.message || 'Server error');
    }));
  }

  private getFilterValue(value: any): string {
    if (value === null || value === undefined) return '';

    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed === '' ? '' : trimmed;
    }

    if (typeof value === 'number') return value.toString();

    if (typeof value === 'object') {

      try {
        if ('id' in value && 'value' in value) {
          if (value.value !== null && value.value !== undefined) {
            const valueStr = String(value.value).trim();
            return valueStr === '' ? '' : valueStr;
          }
        }

        if ('value' in value) {
          if (value.value !== null && value.value !== undefined) {
            const valueStr = String(value.value).trim();
            return valueStr === '' ? '' : valueStr;
          }
        }

        if (value.toString && typeof value.toString === 'function' && value.toString() !== '[object Object]') {
          const valueStr = value.toString().trim();
          return valueStr === '' ? '' : valueStr;
        }
      } catch (error) {
        console.error('Error processing filter value:', error);
      }
    }

    return '';
  }
}
